<template>
  <div>
    <div v-if="pending" class="page__columns-inner container-inner">
      <div class="page__main-col">
        <BasePreloader/>
      </div>
    </div>
    <main v-else class="cooperation-page">
      <p v-if="interactionId <= 0 || !stage.step">Interaction was not found</p>

      <template v-else>
        <HeaderSection
          :vacancy="vacancy"
          :isEmployer="isEmployer"
        />

        <div class="d-flex">
          <div class="cooperation-page__body">
            <Steps
              :current-step="stage.step"
              :title="stage.title"
              :max="stages.length"
              :description-shown="isDescShown && stage.description"
              @click="isDescShown = true"
            />

            <DescriptionSection
              v-if="isDescShown && stage.description"
              :text="stage.description"
              @close="isDescShown = false"
            />

            <ToastSection
              :stage="stage"
              :isAgent="isAgent"
              :isJobSeeker="isJobSeeker"
              :isEmployer="isEmployer"
            />

            <FilesWrapper
              :files="files"
              :todo="stage.what_todo"
              :step="stage.step"
              :steps="stages.length"
              :status="stage.status"
              :readonly="!canLoadFiles"
              :stageExpire="stageExpire"
              :isEmployer="isEmployer"
              :isLoading="isLoading"
              @add="addFile($event)"
              @remove="removeFile($event)"
              @decline="decline()"
              @ready="ready()"
              @toggleTicketVisibility="toggleTicketVisibility($event)"
            />
          </div>

          <StickyContainer
            v-if="windowSizeMixin.width >= 1024 && showChat"
            class="cooperation-page__chat"
          >
            <Chat
              :user="user"
              :isEmployer="isEmployer"
              :interactionId="interactionId"
              :stepIsReadonly="stepIsReadonly"
              :acceptPending="acceptPending"
              :isLoading="isLoading"
              @init="chat = $event"
              @decline="decline()"
              @finish="finish()"
            />
          </StickyContainer>

          <MobileChat
            v-else
            :user="user"
            :isEmployer="isEmployer"
            :interactionId="interactionId"
            :stepIsReadonly="stepIsReadonly"
            :acceptPending="acceptPending"
            :isLoading="isLoading"
            @init="chat = $event"
            @decline="decline()"
            @finish="finish()"
          />
        </div>
      </template>
    </main>
  </div>

</template>

<script>
  import { http } from '../../axios';
  import windowSizeMixin from '../../mixins/windowSizeMixin';

  import Steps from '../../components/cooperation/Steps';
  import ToastSection from '../../components/cooperation/ToastSection';
  import HeaderSection from '../../components/cooperation/HeaderSection';
  import DescriptionSection from '../../components/cooperation/DescriptionSection';
  import FilesWrapper from '../../components/cooperation/filesSection/FilesWrapper';

  import Chat from '../../components/chat/Chat';
  import MobileChat from '../../components/chat/MobileChat';
  import StickyContainer from '../../components/StickyContainer';

  export default {
    components: {
      Steps,
      HeaderSection,
      ToastSection,
      DescriptionSection,
      FilesWrapper,
      Chat,
      MobileChat,
      StickyContainer
    },
    mixins: [ windowSizeMixin ],

    data() {
      return {
        chat: null,
        timeout: null,
        isUnmounted: false,
        showChat: true,

        error: null,
        pending: false,
        isLoading: false,

        interactionId: -1,
        isDescShown: true,

        stages: [],
        stage: {},
        vacancy: null,
        stageExpire: null,

        files: {
          sent: [],
          received: []
        },
        acceptPending: false
      };
    },

    computed: {
      user() {
        return this.$store.state.user.profile;
      },

      isJobSeeker() {
        return (this.user || {}).user_type === 1;
      },

      isAgent() {
        return (this.user || {}).user_type === 2;
      },

      isEmployer() {
        return (this.user || {}).user_type === 3;
      },

      stepForJobSeeker() {
        const stage = (this.stage || {});
        return (stage.user_type === 0 && stage.status === 0) || (stage.user_type === 1 && stage.status === 1);
      },

      stepForAgent() {
        const stage = (this.stage || {});
        return (stage.user_type === 0 && stage.status === 0) || (stage.user_type === 1 && stage.status === 1);
      },

      stepForEmployer() {
        const stage = (this.stage || {});
        return (stage.user_type === 1 && stage.status === 0) || (stage.user_type === 0 && stage.status === 1);
      },

      stepForMe() {
        const isForJobSeeker = this.isJobSeeker && this.stepForJobSeeker;
        const isForAgent = this.isAgent && this.stepForAgent;
        const isForEmployer = this.isEmployer && this.stepForEmployer;

        return isForJobSeeker || isForAgent || isForEmployer;
      },

      stepIsReadonly() {
        const model = this.stage.interaction_model || {};
        return model.canceled === 1 || model.declined === 1;
      },

      canLoadFiles() {
        // if (!this.stage.status) this.canLoadFiles = true;
        // if (this.stage.status === 1 && this.isEmployer) this.canLoadFiles = true;

        if (this.stepIsReadonly) return false;
        return !this.isLoading && (this.stepForMe);
      }
    },

    watch: {
      '$route.params.interactionId'() {
        this.onParamsChanged();
      }
    },

    methods: {
      _catch(error) {
        this.isLoading = false;
        this.error = error.response && error.response.message || error.message;
      },

      onParamsChanged() {
        this.interactionId = Number(this.$route.params.interactionId);
        this.load();
      },

      async load() {
        this.error = null;
        this.pending = true;

        await this.loadSteps();
        await this.loadFiles();

        this.pending = false;

        this.timeout = setTimeout(() => this.loadUpdates(), 5000);
      },

      loadSteps(scratch = true) {
        if (scratch) {
          this.stage = {};
          this.stages = [];
          this.stageExpire = null;
        }

        return http.get(`/api/interaction/get-all-steps`, {
          params: {
            interaction_id: this.interactionId
          }
        })
          .then(({ data }) => {
            this.stages = data.data;
            this.stage = data.now_status;
            this.vacancy = data.vacancy_information;
            this.stageExpire = new Date(parseInt(data.time_for_step, 10) * 1000);

            this.assignStep();
          })
          .catch(this._catch);
      },

      loadFiles(scratch = true) {
        if (scratch) {
          this.files.sent = [];
          this.files.received = [];
        }

        return http.get(`/api/interaction/get-files`, {
          params: {
            interaction_id: this.interactionId
          }
        })
          .then(({ data }) => this._loadFiles(data.data))
          .catch(this._catch);
      },

      _loadFiles(list) {
        const files = {
          sent: [],
          received: []
        };

        list.forEach((file) => {
          const isSent = file.customer_id === this.user.id;
          files[ isSent ? 'sent' : 'received' ].push(file);
        });

        this.files = files;
      },

      loadUpdates() {
        if (this.isUnmounted) return;

        return http.get(`/api/interaction/update-info`, {
          params: {
            interaction_id: this.interactionId
          }
        })
          .then(({ data }) => {
            // Load chat
            if (this.chat && (data.data.chat || {}).chat) {
              this.chat._loadMessages(data.data.chat.chat).forEach((msg) => {
                const isExists = this.chat.messages.filter((m) => m.id === msg.id).length > 0;
                if (!isExists) this.chat.messages.push(msg);
              });
              this.chat.scrollTo();
            }

            // Load files
            this._loadFiles(data.data.files);

            // Load step data
            this.stage = Object.assign(
              {},
              this.stage,
              this.getStepByNumber(data.data.now_status.step),
              data.data.now_status
            );

            setTimeout(() => this.loadUpdates(), 5000);
          })
          .catch(this._catch);
      },

      assignStep(step = this.stage.step) {
        step = Math.min(step, this.stages.length);
        this.stage = Object.assign(this.stage, this.getStepByNumber(step), { step });
      },

      getStepByNumber(step) {
        return this.stages.filter((stage) => stage.step_number === step)[ 0 ];
      },

      addFile({ file }) {
        const data = new FormData();
        data.append('file', file);
        data.append('interaction_id', this.interactionId);

        this.isLoading = true;

        return http.post(`/api/interaction/add-file`, data).then(({ data }) => {
          this.isLoading = false;
          return this.files.sent.push(data.data);
        }).catch((error) => {
          this.isLoading = false;
          this.error = error.response && error.response.message || error.message;
        });
      },

      removeFile({ file, index }) {
        if (!file.id) return;
        this.files.sent.splice(index, 1);

        this.isLoading = true;

        return http.post(`/api/interaction/remove-file`, null, {
          params: {
            interaction_id: this.interactionId,
            file_id: file.id
          }
        }).then(({ data }) => {
          this.isLoading = false;
          return data.data;
        }).catch((error) => {
          this.isLoading = false;
          this.files.sent.push(file);
          this.error = error.response && error.response.message || error.message;
        });
      },

      ready() {
        this.isLoading = false;

        let type = 'employee';
        if (this.isJobSeeker) type = 'employee';
        if (this.isAgent) type = 'employee';
        if (this.isEmployer) type = 'employer';

        if (this.isEmployer) this.assignStep(this.stage.step + 1);
        this.isLoading = true;

        return http.post(`/api/interaction/accept-step-from-${type}`, null, {
          params: {
            interaction_id: this.interactionId
          }
        })
          .then(() => {
          this.isLoading = false;
            this.stage.status = this.isJobSeeker || this.isAgent ? 1 : 2;
          })
          .catch(this._catch);
      },

      decline() {
        this.isLoading = true;

        return http.delete(`/api/interaction/decline-step-from-employer`, {
          params: {
            interaction_id: this.interactionId
          }
        })
          .then(() => {
            this.isLoading = false;
            this.stage = {
              ...this.stage,
              status: 3,
              interaction_model: {
                ...this.stage.interaction_model,
                declined: 1
              }
            };
          })
          .catch(this._catch);
      },

      finish() {
        this.isLoading = true;
        this.acceptPending = true;

        return http.post(`/api/interaction/recruit`, {
          interaction_id: this.interactionId
        })
          .then(() => {
            this.isLoading = false;
            this.stage = {
              ...this.stage,
              status: 3,
              interaction_model: {
                ...this.stage.interaction_model,
                canceled: 1
              }
            };
          })
          .catch(this._catch)
          .finally(() => {
            setTimeout(() => {
              this.isLoading = false;
              this.acceptPending = false;
            }, 1000)
          });
      },

      toggleTicketVisibility(flag) {
        if (!flag) {
          this.showChat = false;
          setTimeout(() => this.showChat = true);
        }
      }
    },

    mounted() {
      this.isUnmounted = false;
      this.onParamsChanged();
    },

    destroyed() {
      clearTimeout(this.timeout);
      this.timeout = null;
      this.isUnmounted = true;
    }
  };
</script>
